import { IconButton } from '@creditornot/cb-button';
import {
  InfoCircleFilled,
  Market,
  MoreHorizontal,
  MessageBubbleSmiley,
  TaskCheckListChecked,
} from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';
import useUser from 'common/hooks/useUser';
import { Typography } from 'common/typography';
import { isDefined } from 'common/utils';
import { reportMenuApiRevertToCme } from 'modules/menu/behavioral';
import { useSharedMenuLink } from 'modules/menu/hooks/useSharedMenuLink';
import { deleteSharedMenu } from 'modules/shared-menu/api';
import { getUrlEstFaqLm } from 'modules/shared-menu/constants';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOnboardingStepsContext } from 'modules/shared-menu/contexts/OnboardingStepsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useIntegrationStatus } from 'modules/shared-menu/hooks/useIntegrationStatus';
import { buildGetSelectionAllQueryKey } from 'modules/shared-menu/hooks/useSelectionData';

import { ImportFromXmenuModal } from './ImportFromXmenuModal';

const Root = styled(Popover)`
  margin-block-start: 4px;
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px;
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  & + & {
    margin-block-start: 16px;
  }
`;

type Action = {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
};

type ActionGroup = {
  title: string;
  actions: Action[];
};

export const SingleVenueActions = () => {
  const intl = useIntl();

  const { isEmployee } = useUser();
  const { vlmeConfig } = useSelectionInfoContext();
  const { externalHelpCenterLinkEnabled } = useOfferingMenuFeaturesContext();
  const { resetOnboarding } = useOnboardingStepsContext();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isXmenuModalOpen, setIsXmenuModalOpen] = useState(false);
  const [isEnablePosIntegrationModalOpen, setIsEnablePosIntegrationModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const allowXmenuImport = useMemo(() => {
    if (!isEmployee) {
      return false;
    }
    const venue = vlmeConfig?.venue;
    if (!venue) {
      return false;
    }
    return venue.status === 'VENUE_UNPUBLISHED';
  }, [vlmeConfig?.venue, isEmployee]);

  const { sharedMenuLink } = useSharedMenuLink();

  const { mutateAsync: executeDeleteSelection, isLoading: isDeletingSelection } = useMutation(() =>
    deleteSharedMenu(sharedMenuLink?.selection_id ?? ''),
  );

  const { isFullMenuIntegrated } = useIntegrationStatus(vlmeConfig?.venue?.id);

  const rollbackToCme = useCallback(async () => {
    try {
      await executeDeleteSelection();

      setIsEnablePosIntegrationModalOpen(false);

      // invalidate VLMA-related queries
      queryClient.invalidateQueries(['shared-menu-link', vlmeConfig?.venue.id]);
      const selectionAllQueryKey = buildGetSelectionAllQueryKey(sharedMenuLink?.selection_id ?? '', false);
      queryClient.invalidateQueries(selectionAllQueryKey);
      reportMenuApiRevertToCme(vlmeConfig?.venue.id ?? '');

      toast.success('Successfully reverted to Menu Editor');
    } catch (error) {
      toast.error(error?.message || 'Something went wrong', { duration: Infinity });
    }
  }, [executeDeleteSelection, queryClient, sharedMenuLink?.selection_id, vlmeConfig?.venue.id]);

  const availableActions: ActionGroup[] = useMemo(() => {
    if (!vlmeConfig?.venue) {
      return [];
    }

    const actionGroups = [];

    if (!isEmployee) {
      const actions = [
        {
          icon: <TaskCheckListChecked />,
          label: intl.formatMessage({
            id: 'listing-manager.selections.venue-editor.support.action-label',
          }),
          onClick: () => {
            resetOnboarding();
            setIsPopoverOpen(false);
          },
        },
      ];

      if (externalHelpCenterLinkEnabled) {
        actions.push({
          icon: <MessageBubbleSmiley />,
          label: intl.formatMessage({
            id: 'listing-manager.selections.venue-editor.help-center.action-label',
          }),
          onClick: () => {
            window.open(getUrlEstFaqLm(intl.locale), '_blank');
            setIsPopoverOpen(false);
          },
        });
      }

      actionGroups.push({
        title: intl.formatMessage({ id: 'listing-manager.selections.venue-editor.actions.support.title' }),
        actions,
      });
    }

    if (isEmployee) {
      actionGroups.push({
        title: intl.formatMessage({ id: 'listing-manager.selections.venue-editor.actions.ops.title' }),
        actions: [
          allowXmenuImport
            ? {
                icon: <Market />,
                label: intl.formatMessage({
                  id: 'listing-manager.selections.venue-editor.xmenu-import.action-label',
                }),
                onClick: () => {
                  setIsXmenuModalOpen(true);
                  setIsPopoverOpen(false);
                },
              }
            : undefined,
          isFullMenuIntegrated
            ? {
                icon: <InfoCircleFilled />,
                label: 'Enable POS Integration',
                onClick: () => setIsEnablePosIntegrationModalOpen(true),
              }
            : undefined,
        ].filter(isDefined),
      });
    }

    return actionGroups;
  }, [
    allowXmenuImport,
    intl,
    isEmployee,
    isFullMenuIntegrated,
    vlmeConfig?.venue,
    resetOnboarding,
    externalHelpCenterLinkEnabled,
  ]);

  if (availableActions.length === 0 || availableActions.every(group => group.actions.length === 0)) {
    return null;
  }

  return (
    <>
      <Root
        placement="bottom-end"
        open={isPopoverOpen}
        renderArrow={false}
        onOpenChange={() => {
          setIsPopoverOpen(false);
        }}
        content={
          <PopoverContent>
            {availableActions.map(group => (
              <StyledMenuItemGroup key={group.title} title={group.title}>
                {group.actions.map(action => (
                  <MenuActionItem key={action.label} icon={action.icon} onClick={action.onClick} variant="black">
                    {action.label}
                  </MenuActionItem>
                ))}
              </StyledMenuItemGroup>
            ))}
          </PopoverContent>
        }
      >
        {({ refProps }) => (
          <IconButton {...refProps} size="tiny" variant="brand" onClick={() => setIsPopoverOpen(oldState => !oldState)}>
            <MoreHorizontal />
          </IconButton>
        )}
      </Root>
      <ImportFromXmenuModal open={isXmenuModalOpen} onClose={() => setIsXmenuModalOpen(false)} />
      <ConfirmModal
        header="Are you sure?"
        description={<Typography.Body3>This will revert this venue back to Menu Editor</Typography.Body3>}
        onCancel={() => setIsEnablePosIntegrationModalOpen(false)}
        onConfirm={rollbackToCme}
        open={isEnablePosIntegrationModalOpen}
        loading={isDeletingSelection}
      />
    </>
  );
};
